<template>
  <div class="flex items-center justify-between md:hidden">
    <PulseIconButton icon="IconClose" variant="secondary" size="large" @click="handleClose" />
    <div class="text-typography-primary flex-grow text-center">Filters</div>
    <AddFilterButton
      v-if="!filters.options.value.hideAddButton"
      :filter-options="filterOptions"
      :selected-options="selectedOptions"
      :disabled="isAddFilterDisabled"
      @add-filter="handleOptionClick"
    />
  </div>
  <div
    class="px-6 py-3 rounded-lg justify-between flex flex-col gap-2 bg-neutral-white md:w-[750px] w-full"
    @keyup.enter="handleEnterPress"
  >
    <div
      v-if="selectedOptions.length === 0"
      :style="{ fontSize: '14px', padding: '8px 0' }"
      class="text-center md:text-left text-typography-primary"
    >
      {{ t('messages.noFilters') }}
    </div>
    <div v-for="(option, index) in selectedOptions" :key="option.field" @dragover.prevent @drop="handleDrop(index)">
      <FilterOptionRow
        :selected-filter="option"
        :field-options="filterOptions"
        :is-first="index === 0"
        :index="index"
        :all-selected-fields="selectedOptions.map(f => f.field)"
        :available-logical-operators="availableLogicalOperators as LogicalOperator[]"
        :start-title="filters.options.value.startTitle"
        :hide-delete-button="filters.options.value.hideDeleteButton"
        :hide-operator="filters.options.value.hideOperator"
        @delete-filter="handleDeleteFilter"
        @update-filter="handleUpdateFilter"
        @start-drag="handleStartDrag"
      />
    </div>

    <hr class="border-t border-neutral-200" style="border-top-width: 2px" />
    <div class="flex justify-between items-center py-2 w-full">
      <AddFilterButton
        v-if="!filters.options.value.hideAddButton"
        class="hidden md:block"
        :filter-options="filterOptions"
        :selected-options="selectedOptions"
        @add-filter="handleOptionClick"
      />
      <div class="flex gap-2 ml-auto">
        <PulseButton
          v-if="selectedOptions.length > 0 && !filters.options.value.hideClearButton"
          class="w-[96px]"
          variant="secondary"
          size="small"
          @click="handleClearFilters"
        >
          {{ t('buttons.clearAll') }}
        </PulseButton>

        <PulseButton
          v-if="selectedOptions.length > 0"
          ref="applyButton"
          :disabled="!canApply"
          class="w-[96px]"
          :style="{ opacity: canApply ? '1' : '0.5', cursor: canApply ? 'pointer' : 'not-allowed' }"
          variant="primary"
          size="small"
          @click="applyFilters"
        >
          {{ t('buttons.apply') }}
        </PulseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { FilterQuery, FilterConfig } from '@monolog/shared/types';
import { useI18n } from 'vue-i18n';
import { LogicalOperator } from '@constants';
import { cloneDeep } from 'lodash-es';

const props = defineProps<{
  filters: ReturnType<typeof useFilters>;
}>();

const { t } = useI18n({ useScope: 'local' });
const selectedOptions = ref<FilterQuery[]>([]);
const filterOptions = ref<FilterConfig[]>([]);
const applyButton = ref<HTMLButtonElement | null>(null);
const draggingIndex = ref<number | null>(null);

const emit = defineEmits<{
  (event: 'close'): void;
}>();

onMounted(() => {
  selectedOptions.value = [...props.filters.appliedFilters.value, ...props.filters.options.value.addedFilters];
  filterOptions.value = props.filters.filterOptions.value;
});

props.filters.events.on(props.filters.emits.LOADED, ({ filterOptions: newFilters }) => {
  filterOptions.value = newFilters;
});

props.filters.events.on(props.filters.emits.CHANGE, ({ filters: newFilters }) => {
  selectedOptions.value = newFilters;
});

const isAddFilterDisabled = computed(() => {
  return selectedOptions.value.length > 10;
});

const availableLogicalOperators = computed<LogicalOperator[]>(() => {
  if (selectedOptions.value.length <= 2) return [LogicalOperator.AND, LogicalOperator.OR];
  const operators = [selectedOptions.value[1].logicalOperator as LogicalOperator];

  return operators;
});

provide('addFilterDisabled', isAddFilterDisabled);

const applyFilters = () => {
  const cleanedFilters = selectedOptions.value.map((filter, index) =>
    index === 0 ? { ...filter, logicalOperator: undefined } : filter,
  );

  props.filters.applyFilters(cleanedFilters);
  emit('close');
};

const handleEnterPress = () => {
  if (canApply.value) {
    applyFilters();
  }
};

const canApply = computed(() => {
  return (
    selectedOptions.value.length > 0 &&
    selectedOptions.value.every(filter => {
      if (!filter.field || !filter.operator) return false;
      const isEmptyCheck = filter.operator === 'null' || filter.operator === 'NOT_null';

      return isEmptyCheck || (filter.value !== undefined && filter.value !== '');
    })
  );
});

function selectOption(field: string) {
  const filterConfig = filterOptions.value.find(f => f.field === field);
  if (!filterConfig) return;

  const isFirst = selectedOptions.value.length === 0;
  const logicalOperator = isFirst
    ? undefined
    : selectedOptions.value[selectedOptions.value.length - 1].logicalOperator || LogicalOperator.AND;
  const newFilter: FilterQuery = {
    field: filterConfig.field,
    logicalOperator: logicalOperator,
    operator: filterConfig.operators[0]?.value || 'equals',
    type: filterConfig.type,
    value: '',
  };

  selectedOptions.value = [...selectedOptions.value, newFilter];
}

function handleClearFilters() {
  props.filters.clearFilters();
  emit('close');
}

function handleDeleteFilter(index: number) {
  props.filters.removeFilter(index);
  selectedOptions.value.splice(index, 1);
}

function handleUpdateFilter(index: number, updatedFilter: FilterQuery) {
  selectedOptions.value[index] = updatedFilter;
}

function handleOptionClick(option: string) {
  selectOption(option);
}

function handleStartDrag(index: number) {
  draggingIndex.value = index;
}

function handleDrop(dropIndex: number) {
  if (draggingIndex.value === null || draggingIndex.value === dropIndex) return;

  const filters = cloneDeep(selectedOptions.value);
  const fromIndex = draggingIndex.value;

  const [draggedFilter] = filters.splice(fromIndex, 1);
  const droppedOntoFilter = filters[dropIndex];

  filters.splice(dropIndex, 0, draggedFilter);

  if (dropIndex === 0) {
    draggedFilter.logicalOperator = undefined;

    if (droppedOntoFilter) {
      droppedOntoFilter.logicalOperator = selectedOptions.value[fromIndex]?.logicalOperator;
    }
  }

  selectedOptions.value = filters;
  draggingIndex.value = null;
}

function handleClose() {
  emit('close');
}
</script>

<i18n lang="json">
{
  "en": {
    "messages": {
      "noFilters": "There are no filters applied."
    },
    "buttons": {
      "addFilter": "Add Filter",
      "clearAll": "Clear All",
      "apply": "Apply"
    },
    "placeholders": {
      "search": "Search..."
    }
  }
}
</i18n>
